/**
 * Catalog of Stream Hub backend route paths.
 *
 * Keep URL construction here so hooks never assemble route strings themselves.
 * Strapi CMS paths live in `CMS_ROUTES` (`@/lib/strapi/routes`) and are called
 * via `apiClient` with `baseUrl: getCmsApiBase()`.
 */
export const API_ROUTES = {
  profileDetail: "/api/v1/profile",
  authMe: "/api/v1/auth/me",
  acceptPrivacyPolicy: "/api/v1/auth/accept-privacy-policy",
  featureFlags: "/api/v1/feature-flags",
  featureFlagsAll: "/api/v1/feature-flags/all",
  gamesSearch: "/api/v1/games/search",
  gamesFilters: "/api/v1/games/filters",
  gamesRoadmap: "/api/v1/games/roadmap",
  statisticsProgress: "/api/v1/statistics/progress",
  statisticsPersonalized: "/api/v1/statistics/personalized",
  statisticsPersonalizedGames: "/api/v1/statistics/personalized/games",
  adminInternalAccounts: "/api/v1/admin/accounts/internal",
  adminInternalAssignees: "/api/v1/admin/accounts/internal/assignees",
  adminInternalAccount: (id: string) => `/api/v1/admin/accounts/internal/${id}`,
  adminInternalAccountManager: (managerId: string) =>
    `/api/v1/admin/accounts/internal/${managerId}/manager`,
  adminInternalAccountStatus: (id: string, status: "activate" | "deactivate") =>
    `/api/v1/admin/accounts/internal/${id}/${status}`,
  adminInternalRetryStreamStatsSync: (id: string) =>
    `/api/v1/admin/accounts/internal/${id}/retry-stream-stats-sync`,
  adminStreamerAccounts: "/api/v1/admin/accounts/streamers",
  adminStreamerAccount: (id: string) =>
    `/api/v1/admin/accounts/streamers/${id}`,
  adminTags: "/api/v1/admin/tags",
  adminTagsCreate: "/api/v1/admin/tags/create",
  adminTag: (id: number) => `/api/v1/admin/tags/${id}`,
  adminStreamerAccountStatus: (id: string, status: "activate" | "deactivate") =>
    `/api/v1/admin/accounts/streamers/${id}/${status}`,
  adminStreamerResendInvitation: (id: string) =>
    `/api/v1/admin/accounts/streamers/${id}/resend-invitation`,
  adminStreamerResetPassword: (id: string) =>
    `/api/v1/admin/accounts/streamers/${id}/reset-password`,
  adminStreamerRetryStreamStatsSync: (id: string) =>
    `/api/v1/admin/accounts/streamers/${id}/retry-stream-stats-sync`,
  adminStreamStatsBackfill: "/api/v1/admin/accounts/stream-stats-backfill",
  adminStreamerNotes: (id: string) =>
    `/api/v1/admin/accounts/streamers/${id}/notes`,
  adminStreamerAnalytics: "/api/v1/admin/streamer-analytics/streamers",
  adminReferenceData: "/api/v1/admin/reference-data",
  adminChallenges: "/api/v1/admin/challenges",
  adminChallenge: (id: string) => `/api/v1/admin/challenges/${id}`,
  adminChallengeDeactivate: (id: string) =>
    `/api/v1/admin/challenges/${id}/deactivate`,
  adminChallengeSubmissions: (challengeId: string) =>
    `/api/v1/admin/challenges/${challengeId}/submissions`,
  adminChallengeSubmissionCounts: (challengeId: string) =>
    `/api/v1/admin/challenges/${challengeId}/submissions/count`,
  adminChallengeSubmissionApprove: (
    challengeId: string,
    submissionId: string
  ) =>
    `/api/v1/admin/challenges/${challengeId}/submissions/${submissionId}/accept`,
  adminChallengeSubmissionReject: (challengeId: string, submissionId: string) =>
    `/api/v1/admin/challenges/${challengeId}/submissions/${submissionId}/reject`,
  adminChallengeSubmissionCancelPrize: (
    challengeId: string,
    submissionId: string
  ) =>
    `/api/v1/admin/challenges/${challengeId}/submissions/${submissionId}/cancel-prize`,
  adminChallengeSubmissionAward: (challengeId: string, submissionId: string) =>
    `/api/v1/admin/challenges/${challengeId}/submissions/${submissionId}/award`,
  challenge: (id: number | string) => `/api/v1/challenges/${id}`,
  analyticsSummary: "/api/v1/reporting/ga/summary",
  analyticsPageViewsTimeSeries: "/api/v1/reporting/ga/page-views/time-series",
  analyticsLoginsTimeSeries: "/api/v1/reporting/ga/logins/time-series",
  analyticsTopPages: "/api/v1/reporting/ga/top-pages",
  challenges: "/api/v1/challenges",
  challengeSubmissions: (id: number | string) =>
    `/api/v1/challenges/${id}/submissions`,
  challengeMySubmission: (id: number | string) =>
    `/api/v1/challenges/${id}/submissions/mine`,
} as const
